import { CalendarEvent } from './CalendarService';
import { EventDate, isSameDay, isSameYear } from './DateService';

export async function openWebShare(
  event: CalendarEvent,
  start: EventDate,
  end: EventDate,
  url?: string,
  fileUrl?: string
): Promise<void> {
  const title = 'Send an invitation';
  const text = getInvitationText(event, start, end, url);
  const file = fileUrl ? await fetchFile(fileUrl, event.name) : undefined;
  const files = file ? [file] : [];
  try {
    await navigator.share?.({files, title, text});
  } catch (error) {
    console.error('Error sharing:', error);
  }
}

export function getWhatsAppLink(
  event: CalendarEvent,
  start: EventDate,
  end: EventDate,
  url?: string
): string {
  const text = getInvitationMarkdown(event, start, end, url);
  return `https://wa.me/?text=${encodeURI(text)}`;
}

export function isWebShareSupported(): boolean {
  if (!navigator.share) {
    if (window.location.protocol === 'http:') {
      console.warn('navigator.share() is only available in secure contexts');
    } else {
      console.log('This browser does not support navigation.share');
    }
    return false;
  }

  // There is no straightforward API to do this.
  // For now, assume that text/plain is supported everywhere.
  const blob = new Blob(['Hello, world!'], { type: 'text/plain' });
  const file = new File([blob], 'test.txt');
  return Boolean(navigator.canShare?.({ files: [file] }));
}

function getInvitationText(
  event: CalendarEvent,
  start: EventDate,
  end: EventDate,
  url: string
) {
  const { name, description } = event;
  const isSameDayEvent = isSameDay(start, end);
  const isSameYearEvent = isSameYear(start, end);
  return [
    name && [
      isSameDayEvent && isSameYearEvent && (
        `Would you like to come to "${name}" at the Chapel on ${start.weekday}, ${start.day}?`
      ),
      !isSameDayEvent && isSameYearEvent && (
        `Would you like to come to "${name}" at the Chapel from ${start.day} to ${end.day}?`
      ),
      !isSameDayEvent && !isSameYearEvent && (
        `Would you like to come to "${name}" at the Chapel from ${start.shortDay} to ${end.shortDay} ${end.year}?`
      )
    ],

    !name && [
      isSameDayEvent && isSameYearEvent && (
        `Would you like to join me at the Chapel on ${start.weekday}, ${start.day}?`
      ),
      !isSameDayEvent && isSameYearEvent && (
        `Would you like to join me at the Chapel from ${start.day} to ${end.day}?`
      ),
      !isSameDayEvent && !isSameYearEvent && (
        `Would you like to join me at the Chapel from ${start.shortDay} to ${end.shortDay} ${end.year}?`
      )
    ],

    description && [
      '\n\n',
      `Here's what to expect:\n`,
      `${description}`
    ],

    url && [
      '\n\n',
      'You can find out more here:\n',
      url
    ]
  ].flat().filter(Boolean).join('')
}

function getInvitationMarkdown(
  event: CalendarEvent,
  start: EventDate,
  end: EventDate,
  url: string
) {
  const { name, description } = event;
  const isSameDayEvent = isSameDay(start, end);
  const isSameYearEvent = isSameYear(start, end);
  return [
    name && [
      isSameDayEvent && isSameYearEvent && (
         `Would you like to come to _"${name}"_ at the Chapel on ${start.weekday}, ${start.day}?`
      ),
      !isSameDayEvent && isSameYearEvent && (
        `Would you like to come to _"${name}"_ at the Chapel from ${start.day} to ${end.day}?`
      ),
      !isSameDayEvent && !isSameYearEvent && (
        `Would you like to come to _"${name}"_ at the Chapel from ${start.shortDay} to ${end.shortDay} ${end.year}?`
      )
    ],

    !name && [
      isSameDayEvent && isSameYearEvent && (
        `Would you like to join me at the Chapel on ${start.weekday}, ${start.day}?`
      ),
      !isSameDayEvent && isSameYearEvent && (
        `Would you like to join me at the Chapel from ${start.day} to ${end.day}?`
      ),
      !isSameDayEvent && !isSameYearEvent && (
        `Would you like to join me at the Chapel from ${start.shortDay} to ${end.shortDay} ${end.year}?`
      )
    ],

    description && [
      '\n\n',
      `Here's what to expect:\n`,
      `_${description}_`
    ],

    url && [
      '\n\n',
      'You can find out more here:\n',
      url
    ]
  ].flat().filter(Boolean).join('')
}

async function fetchFile(fileUrl: string, fileName): Promise<File> {
  try {
    const response = await fetch(fileUrl);
    const blob = await response.blob();
    const { type } = blob;
    const nameWithExtension = getFileNameWithExtension(blob, fileName);
    const lastModified = Date.now();
    return new File([blob], nameWithExtension, { type, lastModified });
  } catch (error) {
    return undefined;
  }
}

export const types = {
  'application/pdf': 'pdf',
  'audio/flac': 'flac',
  'audio/mpeg': 'mp3',
  'audio/ogg': 'ogg',
  'audio/wav': 'wav',
  'audio/webm': 'weba',
  'audio/x-m4a':'m4a',
  'image/avif': 'avif',
  'image/bmp': 'bmp',
  'image/gif': 'gif',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/svg+xml': 'svg',
  'image/tiff': 'tiff',
  'image/webp': 'webp',
  'image/x-icon': 'ico',
  'text/css': 'css',
  'text/csv': 'csv',
  'text/html': 'html',
  'text/plain': 'txt',
  'video/mp4': 'm4v',
  'video/mpeg': 'mpeg',
  'video/ogg': 'ogv',
  'video/webm': 'webm',
};

function getFileNameWithExtension(blog: Blob, fileName: string): string {
  const type = types[blog.type];
  return [fileName, type].filter(Boolean).join('.');
}
